/* eslint-disable prefer-const */
import { AuthService } from '../services/auth.service';
import { IMPERSONATION_SESSION_STORAGE_KEY } from './constants.impersonation';

const IMPERSONATION_VERIFICATION_MAGIC = 'nimmsta.impersonation';

const IMPERSONATION_ORIGIN_QUERY_PARAM = 'imp-origin';

type ImpersonationData = {
  refresh_token: string;
  access_token: string;
  id_token: string;
};

/**
 * Tries to get the impersonation data from local storage or through a broadcast channel.
 * If the impersonation data is found, it will be passed to the callback function.
 * If no impersonation data is found, the callback function will be called with null.
 *
 * @param configuration - The configuration object for the Nimmsta Commons module.
 * @param callback - The callback function to be called with the impersonation data.
 */
export const tryGetImpersonationData = (): Promise<ImpersonationData | null> => {
  // Check if we have impersonation data in session storage
  const impersonationData = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY);
  if (impersonationData) {
    return Promise.resolve(JSON.parse(impersonationData));
  }

  // Check if postMessage impersonation is enabled (via query parameter)
  const url = new URL(window.location.href);
  const impersonationOrigin = url.searchParams.get(IMPERSONATION_ORIGIN_QUERY_PARAM);
  if (impersonationOrigin !== null) {
    return new Promise<ImpersonationData | null>((resolve, reject) => {
      // Remove the query parameter from the URL
      url.searchParams.delete(IMPERSONATION_ORIGIN_QUERY_PARAM);
      window.history.replaceState({}, '', url.toString());

      // Check if we have an opener window
      if (window.opener) {
        let timeout: ReturnType<typeof setTimeout>;

        // Define the message listener
        const messageListener = (event: MessageEvent) => {
          // Ensure the message is from the expected origin
          if (event.origin !== impersonationOrigin) {

            console.error('Keycloak error: Data received from unexpected origin.');
            return;
          }

          // Ensure the message is from "us"
          if (event.data.source !== IMPERSONATION_VERIFICATION_MAGIC) {

            console.error('Keycloak error: Data received from unexpected source.');
            return;
          }

          // Remove the event listener and clear the timeout
          window.removeEventListener('message', messageListener);
          clearTimeout(timeout);

          // Ensure the message contains the impersonation data
          if (event.data.access_token === undefined) {
            reject(new Error('Keycloak error: Invalid impersonation data received.'));
            return;
          }

          // Call the callback function with the impersonation data
          resolve(event.data);
        };
        window.addEventListener('message', messageListener);

        // Set a timeout to remove the listener if no message is received
        timeout = setTimeout(() => {
          window.removeEventListener('message', messageListener);
          // Call the callback with null and hand back control to the caller
          reject(new Error('Keycloak error: Impersonation data not received in time.'));
        }, 10_000); // 10 seconds

        // Notify the other tab that we are ready to receive the impersonation data
        (window.opener as Window).postMessage('ready', impersonationOrigin);

        // Print a message to the console to indicate that we are waiting for the impersonation data

        console.log('Info: Waiting for impersonation data. Timeout in 10s.');
      } else {

        reject(new Error('Keycloak error: No opener window found for impersonation.'));
      }
    });
  }

  // No impersonation data found
  return Promise.resolve(null);
};

/**
 * Launches the given page in a new tab and sends the impersonation data to it.
 * @param configuration configuration of the NimmstaCommonsModule
 * @param data impersonation data
 * @returns an error if the new tab could not be opened
 */
export const launchImpersonationInNewTab = (
  authService: AuthService,
  data: ImpersonationData,
  targetUrl: URL = new URL(window.location.origin),
): Error | null => {
  // Add timeout to close the listener in case the new tab is not opened
  let timeout: ReturnType<typeof setTimeout>;
  let newTab: Window | null;

  // Define the message listener function
  const messageListener = (event: MessageEvent) => {
    // Ensure the message is from the expected origin
    if (event.origin !== targetUrl.origin) {
      return;
    }

    // Check if the message is the ready message
    if (event.data !== 'ready') {
      return;
    }

    // Clear the timeout
    clearTimeout(timeout);

    // Remove the listener since it's no longer needed
    window.removeEventListener('message', messageListener);

    // Send the impersonation data to the new tab
    authService.getCurrentUser().subscribe({
      next: (user) => {
        newTab!.postMessage({ ...data, source: IMPERSONATION_VERIFICATION_MAGIC, actor: user.email }, targetUrl.origin);
      },
      error: () => {
        newTab!.postMessage({ ...data, source: IMPERSONATION_VERIFICATION_MAGIC }, targetUrl.origin);
      },
    });
  };
  window.addEventListener('message', messageListener);

  // Open the new tab
  targetUrl.searchParams.append(IMPERSONATION_ORIGIN_QUERY_PARAM, window.location.origin);
  newTab = window.open(targetUrl.toString(), '_blank');
  if (!newTab) {
    window.removeEventListener('message', messageListener);
    return new Error('Failed to open new tab, please verify popup are allowed on this page.');
  }

  // Set a timeout to remove the listener if the new tab is not opened
  timeout = setTimeout(() => {
    window.removeEventListener('message', messageListener);
    console.error('Keycloak error: Impersonation data request not received in time.');
  }, 5000);

  return null;
};
